import { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw } from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";
import { apiClient, formatUsd } from "../lib/api";

const STATUSES = ["paid", "shipped", "delivered"];

function StatusBadge({ value }) {
  const map = {
    initiated: "bg-[#FDF3ED] text-[#8C6A5C]",
    pending: "bg-[#FDF3ED] text-[#8C6A5C]",
    completed: "bg-[#E7ECE1] text-[#5D6E4E]",
    paid: "bg-[#E7ECE1] text-[#5D6E4E]",
    shipped: "bg-[#E5D8EB] text-[#5F4A6B]",
    delivered: "bg-[#D6E3DC] text-[#3F5C4D]",
    failed: "bg-[#F5D9D6] text-[#8C3A34]",
    expired: "bg-[#F5D9D6] text-[#8C3A34]",
  };
  return (
    <span className={`inline-block px-2 py-1 text-[10px] font-mono uppercase tracking-widest rounded ${map[value] || "bg-[#FDF3ED] text-[#6B5757]"}`}>
      {value || "—"}
    </span>
  );
}

export default function AdminOrdersPage() {
  const { admin, loading, logout, withAuth } = useAdminAuth();
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  const fetchOrders = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.get("/admin/orders", withAuth());
      setOrders(data);
    } catch {
      toast.error("Failed to load orders.");
    } finally {
      setBusy(false);
    }
  }, [withAuth]);

  useEffect(() => {
    if (admin) fetchOrders();
  }, [admin, fetchOrders]);

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center font-serif text-2xl">Loading…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const setStatus = async (sid, status) => {
    try {
      await apiClient.patch(`/orders/status/${sid}`, { status }, withAuth());
      toast.success(`Marked ${status}.`);
      // Optimistic local update
      setOrders((prev) => prev.map((o) => (o.session_id === sid ? { ...o, status } : o)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed.");
    }
  };

  const visible = filter === "all"
    ? orders
    : orders.filter((o) => (o.status || "").toLowerCase() === filter);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-12">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <div className="eyebrow">Internal</div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-2">Order Operations</h1>
          <p className="text-[#6B5757] mt-2 text-sm">Signed in as <span className="font-mono">{admin.email}</span></p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="admin-refresh-btn"
            onClick={fetchOrders}
            className="flex items-center gap-2 px-3 py-2 border border-[#E5CDBB] rounded-md text-sm hover:bg-[#FDF3ED]"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="flex items-center gap-2 px-3 py-2 border border-[#E5CDBB] rounded-md text-sm hover:bg-[#FDF3ED]"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <span className="text-xs font-mono uppercase tracking-widest text-[#2A1F1F] px-3 py-1.5 bg-[#2A1F1F] text-[#FBF6F1] rounded">Orders</span>
        <Link data-testid="admin-nav-campaigns" to="/admin/campaigns" className="text-xs font-mono uppercase tracking-widest text-[#6B5757] hover:text-[#2A1F1F] px-3 py-1.5 border border-[#E5CDBB] rounded">Campaigns</Link>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {["all", "completed", "paid", "shipped", "delivered", "initiated"].map((f) => (
          <button
            key={f}
            data-testid={`admin-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded ${
              filter === f ? "bg-[#2A1F1F] text-[#FBF6F1]" : "bg-white border border-[#E5CDBB] text-[#4A3535] hover:bg-[#FDF3ED]"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {busy && orders.length === 0 ? (
        <div className="py-16 text-center text-[#6B5757] text-sm">Loading orders…</div>
      ) : visible.length === 0 ? (
        <div data-testid="admin-orders-empty" className="py-16 text-center text-[#6B5757] text-sm">No orders match this filter.</div>
      ) : (
        <div className="overflow-x-auto border border-[#F0DFD0] rounded-lg bg-white">
          <table data-testid="admin-orders-table" className="min-w-full">
            <thead className="bg-[#FDF3ED]">
              <tr>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Order</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Customer</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Items</th>
                <th className="text-right px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Total</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Status</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6B5757]">Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((o) => {
                const short = (o.order_id || "").slice(0, 8).toUpperCase();
                const gift = o.is_gift;
                return (
                  <tr key={o.session_id} data-testid={`admin-order-row-${short}`} className="border-t border-[#F0DFD0]">
                    <td className="px-4 py-4 align-top">
                      <div className="font-mono text-xs text-[#2A1F1F]">{short || "—"}</div>
                      <div className="text-[10px] text-[#6B5757] mt-1">{o.created_at?.slice(0, 10)}</div>
                      {gift && <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#B85C56]">Gift</div>}
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="text-sm text-[#2A1F1F]">{o.email || "—"}</div>
                      {gift && (
                        <div className="text-xs text-[#6B5757] mt-1">
                          → {o.gift_recipient_name} ({o.gift_recipient_email})
                        </div>
                      )}
                      {o.surgery_date && (
                        <div className="text-[10px] text-[#6B5757] mt-1 font-mono uppercase tracking-widest">Surgery {o.surgery_date}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 align-top">
                      <ul className="text-xs text-[#4A3535] space-y-0.5">
                        {(o.items || []).map((it, i) => (
                          <li key={i} className="truncate max-w-[280px]">
                            <span className="text-[#6B5757]">{it.quantity}× </span>{it.name}
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="px-4 py-4 align-top text-right font-mono text-sm">{o.amount ? formatUsd(o.amount) : "—"}</td>
                    <td className="px-4 py-4 align-top"><StatusBadge value={o.status} /></td>
                    <td className="px-4 py-4 align-top">
                      <div className="flex flex-wrap gap-1">
                        {STATUSES.map((s) => (
                          <button
                            key={s}
                            data-testid={`admin-set-status-${short}-${s}`}
                            disabled={o.status === s}
                            onClick={() => setStatus(o.session_id, s)}
                            className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest rounded border ${
                              o.status === s
                                ? "border-[#E5CDBB] text-[#A08D8D] cursor-not-allowed"
                                : "border-[#2A1F1F] text-[#2A1F1F] hover:bg-[#2A1F1F] hover:text-[#FBF6F1]"
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-10 text-xs text-[#6B5757]">
        <Link to="/" className="hover:text-[#2A1F1F]">← Back to storefront</Link>
      </div>
    </div>
  );
}
